import React, { useState, useRef } from "react";
import ReactDOM from "react-dom";
import { Info } from "lucide-react";
import { Link } from "react-router-dom";
import LoadingRow from "./LoadingRow";
import CopyableContainer from "./CopyableContainer";
import LeagueTabHeading from "./LeagueTabHeading";
import PendingDataNotice from "./PendingDataNotice";
import "./styles/tableStyles.css";

const UnmatchedTooltip = ({ players }) => {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const btnRef = useRef(null);

    if (!players || players.length === 0) return null;

    const updateCoords = () => {
        if (!btnRef.current) return;
        const rect = btnRef.current.getBoundingClientRect();
        setCoords({
            top: rect.top,
            left: rect.left + rect.width / 2,
        });
    };

    return (
        <span className="relative inline-flex items-center ml-1">
            <button
                ref={btnRef}
                type="button"
                className="text-text-muted hover:text-forest transition-colors focus:outline-none"
                onMouseEnter={() => { updateCoords(); setOpen(true); }}
                onMouseLeave={() => setOpen(false)}
                onFocus={() => { updateCoords(); setOpen(true); }}
                onBlur={() => setOpen(false)}
                aria-label={`${players.length} player(s) not matched in KTC rankings`}
            >
                <Info className="w-3.5 h-3.5" />
            </button>
            {open && ReactDOM.createPortal(
                <div
                    className="fixed z-[9999] w-64 bg-white border border-border rounded-lg shadow-lg p-3 text-left pointer-events-none"
                    style={{
                        top: coords.top,
                        left: coords.left,
                        transform: "translate(-50%, calc(-100% - 8px))",
                    }}
                >
                    <p className="text-xs font-sans font-bold text-ds-text mb-1">
                        Not found in KTC rankings:
                    </p>
                    <ul className="text-xs text-text-muted font-sans space-y-0.5 mb-2">
                        {players.map((name, i) => (
                            <li key={i}>• {name}</li>
                        ))}
                    </ul>
                    <p className="text-xs text-text-muted font-sans italic">
                        These players are excluded from this team's KTC score.
                    </p>
                </div>,
                document.body
            )}
        </span>
    );
};

const rankBadgeStyle = (rank) => {
    if (rank === 1) return { background: "#fef3c7", color: "#92400e" };
    if (rank === 2) return { background: "#f1f5f9", color: "#475569" };
    if (rank === 3) return { background: "#fed7aa", color: "#c2410c" };
    return { background: "#f3f4f6", color: "#9ca3af" };
};

const rvChipStyle = (rvRank, total) => {
    const third = Math.ceil(total / 3);
    if (rvRank <= third)          return { background: "#dcfce7", color: "#166534" };
    if (rvRank <= third * 2)      return { background: "#fef9c3", color: "#854d0e" };
    return { background: "#fee2e2", color: "#991b1b" };
};

const KtcSkeletonChip = () => (
    <span
        className="animate-pulse"
        aria-hidden="true"
        title="Loading roster value…"
        style={{
            display: "inline-block",
            width: "4.5rem", height: "1.4rem",
            borderRadius: "999px",
            background: "#e5e7eb",
        }}
    />
);

const InlineChange = ({ change, label }) => {
    if (change == null || change === 0) return null;
    const up = change > 0;
    return (
        <span
            title={`${label} ${up ? "improved" : "dropped"} by ${Math.abs(change)} spot(s) vs last week`}
            style={{ fontSize: ".65rem", fontWeight: 800, color: up ? "#16a34a" : "#dc2626", whiteSpace: "nowrap" }}
        >
            {up ? "▲" : "▼"}{Math.abs(change)}
        </span>
    );
};

const normalizeTeamName = (value) => String(value || "").trim().toLocaleLowerCase();

const findKtcEntry = (team, byId, byName) => {
    const teamId = String(team?.team_id || "").trim();
    if (teamId && byId[teamId]) return byId[teamId];
    return byName[normalizeTeamName(team?.team)] || null;
};

const PowerRankingsTable = ({ data, prevData, week, loading, nCompletedWeeks, ktcData, ktcLoading, leagueType }) => {
    // Stable ids prevent duplicate owner/team display labels from colliding.
    const prevPowerRankByTeam = React.useMemo(() => {
        if (!prevData || !Array.isArray(prevData)) return {};
        const map = {};
        prevData.forEach((entry, i) => { map[entry.team_id || entry.team] = i + 1; });
        return map;
    }, [prevData]);

    const ktcRows = Array.isArray(ktcData?.data) ? ktcData.data : null;
    const ktcLookups = React.useMemo(() => {
        const byId = {};
        const byName = {};
        for (const entry of ktcRows || []) {
            const teamId = String(entry.team_id || "").trim();
            if (teamId) byId[teamId] = entry;
            const teamName = normalizeTeamName(entry.team);
            if (teamName) byName[teamName] = entry;
        }
        return { byId, byName };
    }, [ktcRows]);

    const hasKtc = ktcRows !== null;
    // Reserve the column as soon as we know KTC is coming, so it doesn't pop in.
    const showKtcColumn = hasKtc || ktcLoading;
    const total = data?.length ?? 0;
    const matchedKtcTeams = Array.isArray(data)
        ? data.filter((team) => findKtcEntry(team, ktcLookups.byId, ktcLookups.byName)).length
        : 0;
    const ktcJoinFailed = Boolean(
        hasKtc && ktcRows.length > 0 && total > 0 && matchedKtcTeams === 0
    );

    return (
        <CopyableContainer
            title={
                <LeagueTabHeading
                    eyebrow="Power Rankings"
                    title={`Week ${week}`}
                    className="mb-0"
                />
            }
            fileName={`power-rankings-week-${week}`}
        >
            <PendingDataNotice
                dataType="Power Rankings"
                isPending={week > nCompletedWeeks}
            />

            {(ktcData?.error || ktcJoinFailed) && (
                <div role="alert" className="mb-3 flex items-center gap-2 bg-red-50 border border-red-200 border-l-[3px] border-l-red-500 rounded px-3 py-2">
                    <Info className="w-3.5 h-3.5 text-red-600 shrink-0" />
                    <p className="text-xs text-red-700 font-sans font-medium">
                        {ktcData?.error || "KTC roster values loaded, but none matched this league's teams. Refresh the page; if this persists, report the league and week."}
                    </p>
                </div>
            )}

            {/* KTC stale-data notice */}
            {hasKtc && ktcData.data_week !== week && (
                <div className="mb-3 flex items-center gap-2 bg-amber-50 border border-amber-200 border-l-[3px] border-l-amber-400 rounded px-3 py-2">
                    <Info className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    <p className="text-xs text-amber-700 font-sans font-medium">
                        KTC data from Week {ktcData.data_week} — current week not yet scraped.
                    </p>
                </div>
            )}

            <div className="table-rounded-shell">
                <table className="table">
                    <thead>
                        <tr>
                            <th style={{ width: "2.5rem" }}>#</th>
                            <th style={{ textAlign: "left" }}>Owner</th>
                            <th>Power Score</th>
                            {showKtcColumn && <th style={{ textAlign: "left" }}>Roster Value</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {loading || !data ? (
                            <LoadingRow
                                text="Loading Power Rankings..."
                                colSpan={showKtcColumn ? 4 : 3}
                            />
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan={showKtcColumn ? 4 : 3} style={{ textAlign: "center" }}>
                                    No power rankings available.
                                </td>
                            </tr>
                        ) : (
                            data.map((team, index) => {
                                const powerRank = index + 1;
                                const identity = team.team_id || team.team;
                                const prevPowerRank = prevPowerRankByTeam[identity] ?? null;
                                const powerRankChange = prevPowerRank != null ? prevPowerRank - powerRank : null;
                                const ktcEntry = findKtcEntry(
                                    team,
                                    ktcLookups.byId,
                                    ktcLookups.byName
                                );
                                return (
                                    <tr
                                        key={identity || index}
                                        className={index % 2 === 0 ? "even-row" : "odd-row"}
                                    >
                                        {/* Rank badge + WoW power rank change */}
                                        <td>
                                            <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                                                <span style={{
                                                    display: "inline-flex", alignItems: "center",
                                                    justifyContent: "center",
                                                    width: "26px", height: "26px", borderRadius: "50%",
                                                    fontSize: ".8rem", fontWeight: 900,
                                                    ...rankBadgeStyle(powerRank),
                                                }}>
                                                    {powerRank}
                                                </span>
                                                <InlineChange change={powerRankChange} label="Power rank" />
                                            </div>
                                        </td>

                                        {/* Owner + team stacked */}
                                        <td style={{ textAlign: "left" }}>
                                            <span className="team-identity-primary">
                                                {team.owner || team.team}
                                            </span>
                                            {team.owner && team.team ? (
                                                <span className="team-identity-secondary">
                                                    {team.team}
                                                </span>
                                            ) : null}
                                        </td>

                                        {/* Power score */}
                                        <td style={{ fontFamily: "'Courier New', monospace", fontWeight: 700 }}>
                                            {typeof team.value === "number" ? team.value.toFixed(2) : team.value}
                                        </td>

                                        {/* Roster value chip + WoW roster rank change embedded */}
                                        {showKtcColumn && (
                                            <td style={{ textAlign: "left" }}>
                                                {!hasKtc ? (
                                                    <KtcSkeletonChip />
                                                ) : (
                                                <span className="inline-flex items-center gap-1.5">
                                                    {ktcEntry ? (
                                                        <span style={{
                                                            display: "inline-flex", alignItems: "center", gap: ".3rem",
                                                            padding: ".2rem .55rem", borderRadius: "999px",
                                                            fontSize: ".78rem", fontWeight: 800,
                                                            ...rvChipStyle(ktcEntry.ktc_rank, total),
                                                        }}>
                                                            {ktcEntry.ktc_value.toFixed(2)}
                                                            <span style={{ fontSize: ".68rem", opacity: .75 }}>
                                                                #{ktcEntry.ktc_rank}
                                                            </span>
                                                        </span>
                                                    ) : "—"}
                                                    <InlineChange change={ktcEntry?.rank_change ?? null} label="Roster value rank" />
                                                    {ktcEntry?.unmatched_players?.length > 0 && (
                                                        <UnmatchedTooltip players={ktcEntry.unmatched_players} />
                                                    )}
                                                </span>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Legend + methodology link */}
            <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                {showKtcColumn && (
                    <div className="flex items-center gap-2 text-xs font-sans text-text-muted">
                        <span>Roster value:</span>
                        <span style={{ background: "#dcfce7", color: "#166534", padding: ".1rem .45rem", borderRadius: "4px", fontWeight: 700 }}>top ⅓</span>
                        <span style={{ background: "#fef9c3", color: "#854d0e", padding: ".1rem .45rem", borderRadius: "4px", fontWeight: 700 }}>mid</span>
                        <span style={{ background: "#fee2e2", color: "#991b1b", padding: ".1rem .45rem", borderRadius: "4px", fontWeight: 700 }}>bottom ⅓</span>
                        <span className="ml-2">↑↓ = week-over-week change</span>
                    </div>
                )}
                <Link
                    to="/methodology"
                    className="text-xs font-sans text-text-muted hover:text-forest underline underline-offset-2 transition-colors ml-auto"
                >
                    How is KTC Roster Value calculated?
                </Link>
            </div>
        </CopyableContainer>
    );
};

export default PowerRankingsTable;
